import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Plus, X, Briefcase } from "lucide-react";
import { Header } from "@/components/Header";
import { useTheme } from "@/components/theme-provider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { companyJobDescriptions } from "@/lib/company-jds";

type ExperienceLevel = 'entry' | 'mid' | 'senior';

const companies = [
  // Technology Industry
  { 
    id: 'google', 
    name: 'Google', 
    logo: '/assets/company-logos/google.svg',
    industry: 'Technology'
  },
  { 
    id: 'meta', 
    name: 'Meta', 
    logo: '/assets/company-logos/meta.svg',
    industry: 'Technology'
  },
  { 
    id: 'apple', 
    name: 'Apple', 
    logo: '/assets/company-logos/apple.svg',
    logoLight: '/assets/company-logos/apple-light.svg',
    useLightInDark: true,
    industry: 'Technology'
  },
  { 
    id: 'amazon', 
    name: 'Amazon', 
    logo: '/assets/company-logos/amazon.svg',
    logoLight: '/assets/company-logos/amazon-light.svg',
    useLightInDark: true,
    industry: 'Technology'
  },
  { 
    id: 'netflix', 
    name: 'Netflix', 
    logo: '/assets/company-logos/netflix.svg',
    industry: 'Technology'
  },
  { 
    id: 'microsoft', 
    name: 'Microsoft', 
    logo: '/assets/company-logos/microsoft.svg',
    industry: 'Technology'
  },
  // Finance Industry
  { 
    id: 'jpmorgan', 
    name: 'JPMorgan Chase', 
    logo: '/assets/company-logos/jpmorgan.svg',
    logoLight: '/assets/company-logos/jpmorgan-white.svg',
    useLightInDark: true,
    industry: 'Finance'
  },
  // Healthcare Industry
  { 
    id: 'johnson', 
    name: 'Johnson & Johnson', 
    logo: '/assets/company-logos/johnson-johnson.svg',
    industry: 'Healthcare'
  },
  // Retail & E-Commerce Industry
  { 
    id: 'walmart', 
    name: 'Walmart', 
    logo: '/assets/company-logos/walmart.svg',
    industry: 'Retail'
  },
  { 
    id: 'zara', 
    name: 'Zara (Inditex)', 
    logo: '/assets/company-logos/zara.svg',
    industry: 'Retail'
  },
  // Automotive Industry
  { 
    id: 'tesla', 
    name: 'Tesla', 
    logo: '/assets/company-logos/tesla-motors.svg',
    industry: 'Automotive'
  },
  // Energy Industry
  { 
    id: 'exxonmobil', 
    name: 'ExxonMobil', 
    logo: '/assets/company-logos/exxon-mobil.svg',
    industry: 'Energy'
  },
  // Entertainment & Media Industry
  { 
    id: 'warnerbros', 
    name: 'Warner Bros. Discovery', 
    logo: '/assets/company-logos/warner-bros.svg',
    industry: 'Entertainment'
  },
  { 
    id: 'universal', 
    name: 'Universal Music Group', 
    logo: '/assets/company-logos/universal.svg',
    industry: 'Entertainment'
  },
  { 
    id: 'espn', 
    name: 'ESPN', 
    logo: '/assets/company-logos/espn.svg',
    industry: 'Entertainment'
  },
];

const experienceLevels = [
  { value: 'entry', label: 'Entry Level (0-2 years)' },
  { value: 'mid', label: 'Mid Level (2-5 years)' },
  { value: 'senior', label: 'Senior Level (5+ years)' },
];

// Get unique industries for grouping
const industries = [...new Set(companies.map(company => company.industry))];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function SelectCompany() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [customCompany, setCustomCompany] = useState("");
  const [customJobDescription, setCustomJobDescription] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedExperience, setSelectedExperience] = useState<ExperienceLevel>('mid');
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [showJobPreview, setShowJobPreview] = useState(false);

  const isDark = theme === 'dark';

  const handleCompanyClick = (companyId: string) => {
    setSelectedCompany(companyId);
    setShowJobPreview(true);
  };

  const handleCompanySelect = () => {
    if (!selectedCompany) return;
    
    const selectedJobDescription = companyJobDescriptions[selectedCompany][selectedExperience];
    navigate(`/upload-resume`, { 
      state: { 
        company: selectedCompany,
        jobDescription: selectedJobDescription,
        experienceLevel: selectedExperience
      } 
    });
  };

  const handleCustomCompany = () => {
    if (customCompany && customJobDescription) {
      navigate("/upload-resume", {
        state: {
          company: customCompany,
          jobDescription: customJobDescription
        }
      });
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-background to-background/80">
      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>

      <div className="container mx-auto max-w-4xl px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-blue-600 dark:from-white dark:to-white/80 bg-clip-text text-transparent">
            Select Your Target Company
          </h1>
          <p className="text-muted-foreground dark:text-foreground/80 text-lg mb-8">
            Choose the company you're preparing to interview with
          </p>

          <div className="max-w-xs mx-auto">
            <Select
              value={selectedExperience}
              onValueChange={(value: ExperienceLevel) => setSelectedExperience(value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select experience level" />
              </SelectTrigger>
              <SelectContent>
                {experienceLevels.map((level) => (
                  <SelectItem key={level.value} value={level.value}>
                    {level.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </motion.div>

        {/* Display companies by industry */}
        {industries.map(industry => (
          <div key={industry} className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-foreground/90 border-b pb-2">
              {industry} Industry
            </h2>
            <motion.div
              variants={container}
              initial="hidden"
              animate="show"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {companies
                .filter(company => company.industry === industry)
                .map((company) => (
                <motion.div
                  key={company.id}
                  variants={item}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className={cn(
                    "group relative overflow-hidden rounded-xl bg-card p-6 border border-border/50",
                    "transition-all duration-300 hover:bg-accent cursor-pointer",
                    selectedCompany === company.id && "ring-2 ring-primary"
                  )}
                  onClick={() => handleCompanyClick(company.id)}
                >
                  <div className="relative z-10 flex flex-col items-center justify-center space-y-4">
                    <div className="w-32 h-32 p-6 rounded-full bg-background flex items-center justify-center">
                      <img
                        src={company.useLightInDark && isDark && company.logoLight ? company.logoLight : company.logo}
                        alt={`${company.name} logo`}
                        className="w-full h-full object-contain transition-all duration-300"
                      />
                    </div>
                    
                    <h2 className="text-2xl font-semibold">{company.name}</h2>
                    
                    <Button
                      variant="ghost"
                      className="group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
                    >
                      Select Company
                    </Button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        ))}

        {/* Add Custom Company Card */}
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12"
        >
          <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
            <DialogTrigger asChild>
              <motion.div
                variants={item}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={cn(
                  "group relative overflow-hidden rounded-xl bg-card p-6 cursor-pointer border border-dashed border-border/50",
                  "transition-all duration-300 hover:bg-accent",
                  "flex flex-col items-center justify-center min-h-[300px]"
                )}
              >
                <Plus className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium text-muted-foreground">Add Custom Company/JD</p>
              </motion.div>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Custom Company/JD</DialogTitle>
                <DialogDescription>
                  Enter the company name and job description
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Company Name</label>
                  <Input
                    placeholder="Enter company name"
                    value={customCompany}
                    onChange={(e) => setCustomCompany(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Job Description</label>
                  <Textarea
                    placeholder="Paste job description here"
                    value={customJobDescription}
                    onChange={(e) => setCustomJobDescription(e.target.value)}
                    rows={4}
                  />
                </div>
                <Button
                  onClick={handleCustomCompany}
                  disabled={!customCompany || !customJobDescription}
                >
                  Continue
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </motion.div>

        {/* Job Description Preview Dialog */}
        <Dialog open={showJobPreview} onOpenChange={setShowJobPreview}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-foreground">
                <Briefcase className="w-5 h-5" />
                {selectedCompany && companies.find(c => c.id === selectedCompany)?.name} - Job Description
              </DialogTitle>
              <DialogDescription className="text-muted-foreground dark:text-foreground/80">
                {experienceLevels.find(level => level.value === selectedExperience)?.label}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 max-h-[60vh] overflow-y-auto">
              {selectedCompany && (
                <div className="space-y-4">
                  <div className="prose prose-sm dark:prose-invert">
                    <pre className="whitespace-pre-wrap font-sans text-muted-foreground dark:text-foreground/90">
                      {companyJobDescriptions[selectedCompany][selectedExperience]}
                    </pre>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setShowJobPreview(false)}>
                Cancel
              </Button>
              <Button onClick={handleCompanySelect}>
                Continue with this Role
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <Button
            variant="outline"
            onClick={() => navigate("/")}
            className="hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            Back to Home
          </Button>
        </motion.div>
      </div>
    </div>
  );
}